/**
 * Onboarding vocabularies. Chips beat typing wherever the vocabulary is
 * knowable — these lists are that vocabulary. Free text always survives as the
 * escape hatch (as-typed rows, add-your-own chips).
 *
 * Vocabulary rules (FOLLOWUPS §8, JP 7 Aug 2026): front pages carry the
 * highest-base-rate answers, not aesthetic bets; no class-specific chips
 * anywhere; every list is founder-reviewed line by line before deploy.
 */

/** Interests — identity facts. Front page = what most freshmen actually pick. */
export const INTEREST_CHIPS = [
  "gaming",
  "anime & manga",
  "music",
  "sports",
  "gym & fitness",
  "cooking",
  "movies & TV",
  "k-pop",
  "start-ups",
  "fashion",
  "photography",
  "mental health",
];

export const INTEREST_CHIPS_MORE = [
  "sneakers",
  "thrifting",
  "true crime pods",
  "greek life",
  "intramurals",
  "volunteering",
  "fantasy football",
  "astrology",
  "investing",
  "cars",
  "travel",
  "art & design",
];

/** Hobbies — activity facts; the bot reasons about these differently. */
export const HOBBY_CHIPS = [
  "gaming",
  "basketball",
  "lifting",
  "cooking",
  "drawing",
  "editing videos",
  "guitar",
  "running",
];

export const HOBBY_CHIPS_MORE = [
  "volleyball",
  "swimming",
  "hiking",
  "skateboarding",
  "chess",
  "baking",
  "photography",
  "journaling",
  "crochet",
  "LARP",
  "trolling",
];

/** Goals — multi-select like interests/hobbies (8.6). Plain ambitions stated
 * plainly; never class-specific (8.5). */
export const GOAL_CHIPS = [
  "4.0 GPA",
  "make real friends",
  "just survive",
  "land an internship",
  "make money",
  "stay healthy",
  "get fit",
  "join a club",
  "actually sleep",
];

export const MAJOR_CHIPS = [
  "Biology",
  "Business",
  "Computer Science",
  "Nursing",
  "Engineering",
  "Psychology",
  "undeclared & proud",
];

/** Reciprocity beats (8.12) — Froshbot reacting to a pick, scripted, zero model
 * calls. Keyed by exact chip text; absence just means the default hint shows. */
export const REACTIONS: Record<string, string> = {
  gaming: "we'll get along.",
  "anime & manga": "respect.",
  "k-pop": "noted — comeback season is a valid excuse.",
  "start-ups": "founder energy. I'll remember that.",
  lifting: "discipline. I see it.",
  "editing videos": "so you're the friend who makes the recaps.",
  "4.0 GPA": "ambitious. I'll help you hold it.",
  "just survive": "honest. that's all I need.",
  "make real friends": "that's literally what I'm for.",
  "actually sleep": "I'll protect that goal like it's mine.",
};

/** Search-and-pick seed list. The as-typed row is the real answer to coverage —
 * no school list gatekeeps anyone. */
export const SCHOOLS: { name: string; place: string }[] = [
  { name: "UT Austin", place: "Austin, TX" },
  { name: "UT Arlington", place: "Arlington, TX" },
  { name: "UT Dallas", place: "Richardson, TX" },
  { name: "Texas A&M", place: "College Station, TX" },
  { name: "Texas State", place: "San Marcos, TX" },
  { name: "Texas Tech", place: "Lubbock, TX" },
  { name: "University of Houston", place: "Houston, TX" },
  { name: "Rice University", place: "Houston, TX" },
  { name: "Baylor", place: "Waco, TX" },
  { name: "TCU", place: "Fort Worth, TX" },
  { name: "SMU", place: "Dallas, TX" },
  { name: "UNT", place: "Denton, TX" },
  { name: "Austin Community College", place: "Austin, TX" },
  { name: "UCLA", place: "Los Angeles, CA" },
  { name: "USC", place: "Los Angeles, CA" },
  { name: "UC Berkeley", place: "Berkeley, CA" },
  { name: "San Diego State", place: "San Diego, CA" },
  { name: "NYU", place: "New York, NY" },
  { name: "Columbia", place: "New York, NY" },
  { name: "Boston University", place: "Boston, MA" },
  { name: "Northeastern", place: "Boston, MA" },
  { name: "University of Michigan", place: "Ann Arbor, MI" },
  { name: "Michigan State", place: "East Lansing, MI" },
  { name: "Ohio State", place: "Columbus, OH" },
  { name: "Penn State", place: "University Park, PA" },
  { name: "University of Florida", place: "Gainesville, FL" },
  { name: "Florida State", place: "Tallahassee, FL" },
  { name: "UCF", place: "Orlando, FL" },
  { name: "University of Georgia", place: "Athens, GA" },
  { name: "Georgia Tech", place: "Atlanta, GA" },
  { name: "University of Washington", place: "Seattle, WA" },
  { name: "Arizona State", place: "Tempe, AZ" },
  { name: "University of Arizona", place: "Tucson, AZ" },
  { name: "UNC Chapel Hill", place: "Chapel Hill, NC" },
  { name: "NC State", place: "Raleigh, NC" },
  { name: "University of Virginia", place: "Charlottesville, VA" },
  { name: "Virginia Tech", place: "Blacksburg, VA" },
  { name: "University of Illinois", place: "Urbana-Champaign, IL" },
  { name: "Purdue", place: "West Lafayette, IN" },
  { name: "Indiana University", place: "Bloomington, IN" },
  { name: "University of Wisconsin", place: "Madison, WI" },
  { name: "University of Minnesota", place: "Minneapolis, MN" },
  { name: "University of Colorado", place: "Boulder, CO" },
  { name: "University of Oregon", place: "Eugene, OR" },
];

/** Memory-ring phase words — the progress affordance is a relationship, never
 * a step count (O2). Step counts are banned copy. */
export const PHASES = {
  who: "getting to know you",
  fun: "ok, the fun part",
  want: "almost there — the real stuff",
  last: "last one, promise",
} as const;

export type PhaseKey = keyof typeof PHASES;
